import React, { useState, useEffect, useMemo, useRef } from 'react';
import DisplayScreen from './DisplayScreen';
import ControlPanel from './ControlPanel';
import { builtInTemplates } from '../data/templates';
import { defaultBackground } from '../data/backgrounds';
import { useUserData } from '../contexts/UserDataContext';
import { publishState } from '../lib/conferenceStore';

const initialFromTemplate = (tpl) => ({
  title: tpl?.title || '',
  paragraph: tpl?.paragraph || '',
  lowerText: tpl?.lower_text || tpl?.lowerText || '',
});

export const ConferenceScreen = () => {
  const {
    loading,
    allTemplates,
    findTemplate,
    findBackground,
    resolveBackgroundUrl,
    savedState,
    persistState,
    recordTemplateUsage,
  } = useUserData();

  // Core display state
  const [currentTemplate, setCurrentTemplate] = useState('session-starting');
  const initial = initialFromTemplate(builtInTemplates['session-starting']);
  const [title, setTitle] = useState(initial.title);
  const [paragraph, setParagraph] = useState(initial.paragraph);
  const [lowerText, setLowerText] = useState(initial.lowerText);
  const [animationEnabled, setAnimationEnabled] = useState(true);
  const [showControl, setShowControl] = useState(true);
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [targetTime, setTargetTime] = useState('09:33');
  const [targetTimezone, setTargetTimezone] = useState('local');
  const [timerLabel, setTimerLabel] = useState('Session resumes in');
  const [autoCloseOnTimerEnd, setAutoCloseOnTimerEnd] = useState(true);
  const [manualMode, setManualMode] = useState(false);
  const [manualCurrentTime, setManualCurrentTime] = useState('16:36');
  const [backgroundKey, setBackgroundKey] = useState(defaultBackground);

  const restoredRef = useRef(false);

  // Restore saved state once when it loads
  useEffect(() => {
    if (restoredRef.current) return;
    if (loading) return;
    if (!savedState) {
      restoredRef.current = true;
      return;
    }
    restoredRef.current = true;
    if (savedState.template_id) setCurrentTemplate(savedState.template_id);
    if (savedState.title !== undefined) setTitle(savedState.title);
    if (savedState.paragraph !== undefined) setParagraph(savedState.paragraph);
    if (savedState.lower_text !== undefined) setLowerText(savedState.lower_text);
    if (savedState.background_key) setBackgroundKey(savedState.background_key);
    if (typeof savedState.animation_enabled === 'boolean') setAnimationEnabled(savedState.animation_enabled);
    if (typeof savedState.timer_enabled === 'boolean') setTimerEnabled(savedState.timer_enabled);
    if (savedState.target_time) setTargetTime(savedState.target_time);
    if (savedState.target_timezone) setTargetTimezone(savedState.target_timezone);
    if (savedState.timer_label) setTimerLabel(savedState.timer_label);
    if (typeof savedState.manual_mode === 'boolean') setManualMode(savedState.manual_mode);
    if (savedState.manual_current_time) setManualCurrentTime(savedState.manual_current_time);
    if (typeof savedState.auto_close_on_timer_end === 'boolean') setAutoCloseOnTimerEnd(savedState.auto_close_on_timer_end);
  }, [loading, savedState]);

  const handleTemplateChange = (templateId) => {
    setCurrentTemplate(templateId);
    const tpl = findTemplate(templateId);
    if (tpl && templateId !== 'custom') {
      setTitle(tpl.title);
      setParagraph(tpl.paragraph);
      setLowerText(tpl.lower_text);
    }
    recordTemplateUsage(templateId);
  };

  // Resolve background URL (built-in path or API image url)
  const backgroundUrl = useMemo(() => {
    const bg = findBackground(backgroundKey);
    return resolveBackgroundUrl(bg) || resolveBackgroundUrl(findBackground(defaultBackground));
  }, [backgroundKey, findBackground, resolveBackgroundUrl]);

  // Publish state to localStorage + BroadcastChannel for the presenter window
  useEffect(() => {
    publishState({
      title, paragraph, lowerText, animationEnabled,
      timerEnabled, targetTime, targetTimezone, timerLabel, manualMode, manualCurrentTime,
      autoCloseOnTimerEnd,
      backgroundKey, backgroundUrl,
    });
  }, [title, paragraph, lowerText, animationEnabled, timerEnabled,
      targetTime, targetTimezone, timerLabel, manualMode, manualCurrentTime,
      autoCloseOnTimerEnd,
      backgroundKey, backgroundUrl]);

  // Auto-save to backend (debounced)
  const saveTimerRef = useRef(null);
  useEffect(() => {
    if (!restoredRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      persistState({
        template_id: currentTemplate,
        title,
        paragraph,
        lower_text: lowerText,
        background_key: backgroundKey,
        animation_enabled: animationEnabled,
        timer_enabled: timerEnabled,
        target_time: targetTime,
        target_timezone: targetTimezone,
        timer_label: timerLabel,
        manual_mode: manualMode,
        manual_current_time: manualCurrentTime,
        auto_close_on_timer_end: autoCloseOnTimerEnd,
      });
    }, 1500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [currentTemplate, title, paragraph, lowerText, backgroundKey,
      animationEnabled, timerEnabled, targetTime, targetTimezone, timerLabel,
      manualMode, manualCurrentTime, autoCloseOnTimerEnd, persistState]);

  const openPresenterWindow = () => {
    const url = `${window.location.origin}${window.location.pathname}?view=presenter`;
    window.open(url, 'iri-presenter', 'width=1280,height=720,menubar=no,toolbar=no,location=no,status=no');
  };

  const enterFullscreen = () => {
    const el = document.documentElement;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else if (el.requestFullscreen) {
      el.requestFullscreen();
    }
  };

  return (
    <div className="flex h-screen">
      <div className={`${showControl ? 'flex-1' : 'w-full'} transition-all duration-300 display-wrapper`}>
        <DisplayScreen
          title={title}
          paragraph={paragraph}
          lowerText={lowerText}
          animationEnabled={animationEnabled}
          timerEnabled={timerEnabled}
          targetTime={targetTime}
          targetTimezone={targetTimezone}
          timerLabel={timerLabel}
          manualMode={manualMode}
          manualCurrentTime={manualCurrentTime}
          backgroundUrl={backgroundUrl}
        />
      </div>

      {showControl && (
        <div className="w-96 border-l border-gray-800">
          <ControlPanel
            allTemplates={allTemplates}
            currentTemplate={currentTemplate}
            onTemplateChange={handleTemplateChange}
            title={title}
            onTitleChange={setTitle}
            paragraph={paragraph}
            onParagraphChange={setParagraph}
            lowerText={lowerText}
            onLowerTextChange={setLowerText}
            animationEnabled={animationEnabled}
            onAnimationToggle={setAnimationEnabled}
            onTogglePanel={() => setShowControl(false)}
            timerEnabled={timerEnabled}
            onTimerToggle={setTimerEnabled}
            targetTime={targetTime}
            onTargetTimeChange={setTargetTime}
            targetTimezone={targetTimezone}
            onTargetTimezoneChange={setTargetTimezone}
            timerLabel={timerLabel}
            onTimerLabelChange={setTimerLabel}
            manualMode={manualMode}
            onManualModeToggle={setManualMode}
            manualCurrentTime={manualCurrentTime}
            onManualCurrentTimeChange={setManualCurrentTime}
            autoCloseOnTimerEnd={autoCloseOnTimerEnd}
            onAutoCloseToggle={setAutoCloseOnTimerEnd}
            backgroundKey={backgroundKey}
            onBackgroundChange={setBackgroundKey}
            onOpenPresenter={openPresenterWindow}
            onEnterFullscreen={enterFullscreen}
          />
        </div>
      )}

      {!showControl && (
        <button
          onClick={() => setShowControl(true)}
          className="fixed top-4 right-4 z-50 bg-gray-800/80 backdrop-blur-md text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Show Controls
        </button>
      )}
    </div>
  );
};

export default ConferenceScreen;
